import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PlusCircle, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient } from "@/lib/api";
import { CategoryColors } from "@/lib/colors";
import { fmtMoney } from "@/lib/money";
import { isAdmin, canSubmitMultiple, userCategories } from "@/lib/roles";
import { PageHeader, EmptyState, PageLoader, CategoryBadge, Card } from "@/components/shared/ui";
import FacilityPicker from "@/components/shared/FacilityPicker";

export default function UnitDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const admin = isAdmin(user);
  const [unit, setUnit] = useState(null);
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [unitData, estData] = await Promise.all([
          apiClient.get(`/units/${id}`),
          apiClient.get(`/estimations?unit_number=${id}&limit=20`),
        ]);
        setUnit(unitData);
        setEstimations(estData.estimations || []);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const goSubmit = () => {
    if (canSubmitMultiple(user)) setShowPicker(true);
    else navigate(`/estimation/${userCategories(user)[0] || "structure"}?unit_number=${id}`);
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={`${t("unit")} ${id}`}
        subtitle={[unit?.block ? `${t("block")}: ${unit.block}` : null, unit?.floor ? `${t("floor")}: ${unit.floor}` : null].filter(Boolean).join(" · ")}
        actions={
          <button
            data-testid="unit-submit-btn"
            onClick={goSubmit}
            className="flex items-center gap-2 rounded-xl bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
          >
            <PlusCircle className="h-4 w-4" /> {t("submit_new_estimation")}
          </button>
        }
      />

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">{t("estimation_history")}</h2>
        <span className="text-sm text-slate-400">
          {estimations.length} {t("total_label")}
        </span>
      </div>

      {estimations.length === 0 ? (
        <EmptyState icon={FileText} title={t("no_estimations")} subtitle={t("first_estimation")} />
      ) : (
        <div className="space-y-3">
          {estimations.map((est) => (
            <Card
              key={est.id}
              className="cursor-pointer p-4 transition-shadow hover:shadow-md"
            >
              <div data-testid={`estimation-card-${est.id}`} onClick={() => navigate(`/estimation/view/${est.id}`)}>
                <div className="mb-2 flex items-center justify-between">
                  <CategoryBadge label={t(est.category)} color={CategoryColors[est.category]} />
                  <span className="text-xs text-slate-400">{formatDate(est.created_at)}</span>
                </div>
                <div className="text-sm font-medium text-slate-800">{est.engineer_name}</div>
                <div className="mt-1 text-xs text-slate-500">
                  {est.estimation_number ? `${t("estimation_num")}: ${est.estimation_number} · ` : ""}
                  {est.rows?.length || 0} {t("rows_label")}
                  {est.final_total ? ` · ${t("final_total")}: ${fmtMoney(est.final_total, est.currency)}` : ""}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showPicker ? (
        <FacilityPicker
          categories={userCategories(user)}
          title={t("which_section")}
          onClose={() => setShowPicker(false)}
          onSelect={(cat) => navigate(`/estimation/${cat}?unit_number=${id}`)}
        />
      ) : null}
    </div>
  );
}
